/**
 * Repeat choices shared by the date picker and the task schedule section:
 * which recurrences exist and how each one reads in the app language.
 */
import { formatDate, translate } from "@/i18n";
import { panelsTaskScheduleMessages } from "@/i18n/messages/panels-task-schedule";

export interface RepeatOption {
  rrule: "daily" | "weekdays" | "weekly" | "biweekly" | "monthly";
  rruleDay?: number; // 0-6 for weekly (Mon=0, Sun=6), 1-28 for monthly
}

/** A weekday's name; `day` counts from Monday = 0, like the backend's rruleDay. */
export function weekdayName(
  day: number,
  weekday: "long" | "short" | "narrow" = "long",
): string {
  // 1 January 2024 was a Monday.
  return formatDate(new Date(2024, 0, 1 + day), { weekday });
}

/** Map JS getDay() (Sun=0..Sat=6) to backend rruleDay (Mon=0..Sun=6) */
function jsDayToBackendDay(jsDay: number): number {
  return jsDay === 0 ? 6 : jsDay - 1;
}

/** The repeat choices the picker offers for `date`. */
export function buildRepeatOptions(date: Date): RepeatOption[] {
  return [
    { rrule: "daily" },
    { rrule: "weekly", rruleDay: jsDayToBackendDay(date.getDay()) },
    { rrule: "weekdays" },
    { rrule: "monthly", rruleDay: date.getDate() },
  ];
}

/** How a repeat choice reads, in the app language at call time. */
export function repeatLabel(option: RepeatOption): string {
  const m = panelsTaskScheduleMessages;
  switch (option.rrule) {
    case "daily":
      return translate(m, "repeatDaily");
    case "weekdays":
      return translate(m, "repeatWeekdays");
    case "weekly":
      return translate(m, "repeatWeekly", {
        day: weekdayName(option.rruleDay ?? 0),
      });
    case "biweekly":
      return translate(m, "repeatBiweekly", {
        day: weekdayName(option.rruleDay ?? 0),
      });
    case "monthly":
      return translate(m, "repeatMonthly", { day: option.rruleDay || 1 });
  }
}
